import {
  CustomFieldDefinition,
  CustomFieldUsage,
  LabelValues,
  PermissionType,
  ShareTargetType,
} from '@alucio/aws-beacon-amplify/src/models'
import {
  DNABox, DNAButton, DNACheckbox, DNAContextMenu, DNADivider, DNAModal, DNAText,
  Iffy, luxColors,
} from '@alucio/lux-ui'
import React, { useMemo } from 'react'
import { FlatList, StyleSheet } from 'react-native'
import { FolderORM } from 'src/types/orms'
import { DNAModalVariant } from '../DNAConnectedModal'
import { isEveryoneType, isFilterType, isUserType, ShareFilter, ShareFolderMode } from './DNAShareFolderModal'
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors'
import isEqual from 'lodash/isEqual'
import useFeatureFlags from 'src/hooks/useFeatureFlags/useFeatureFlags'
import { useIndexedTenantCustomFields } from 'src/state/redux/selector/tenant'

const styles = StyleSheet.create({
  icon: {
    height: 25,
    width: 25,
  },
  hoveredBackgroundColor: {
    backgroundColor: luxColors.hoveredBackground.primary,
  },
  menu: {
    maxHeight: 300,
  },
  titleText: {
    color: colors['color-gray-600'],
    fontSize: 14,
    lineHeight: 16,
  },
  searchBarStyle: {
    backgroundColor: colors['color-text-white'],
    // TODO: Color not available on the theming yet
    borderColor: luxColors.disabled.secondary,
    borderRadius: 4,
    width: 316,
    overflow: 'hidden',
  },
  searchBarWrapper: {
    marginTop: 20,
    flex: 1,
  },
  recordRow: {
    flex: 1,
    flexDirection: 'row',
    height: 52,
    paddingBottom: 8,
    paddingLeft: 16,
    paddingRight: 16,
    paddingTop: 8,
  },
  noResults: {
    marginLeft: 4,
    marginBottom: 6,
    marginTop: 6,
  },
  resultsWrapperStyle: {
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: 0,
    paddingRight: 0,
  },
  customFieldsText: {
    color: colors['color-gray-300'],
    fontSize: 12,
    lineHeight: 20,
  },
  folderMargins: {
    marginLeft: 24,
    marginRight: 12,
  },
  modal: {
    maxHeight: '90vh',
    paddingVertical: 24,
  },
  modalBody: {
    margin: 24,
  },
})

interface PermissionsListProps {
  currentItems: ShareFilter[]
  onRemoveItem: (id: string) => void
  togglePermission: (id: string) => void
}

const PermissionsList: React.FC<PermissionsListProps> = React.memo((props) => {
  const { currentItems, onRemoveItem, togglePermission } = props
  const indexedCustomFields = useIndexedTenantCustomFields({
    usages: { internalUsages: [CustomFieldUsage.USER_FILTER] },
  });

  const items = useMemo(() => {
    const shareFilters: ShareFilter[] = []
    if (currentItems) {
      currentItems.forEach((item) => {
        if (isUserType(item.filter)) {
          shareFilters.push(item)
        }
      })
    }
    shareFilters.sort((a, b) => {
      if (isUserType(a.filter) && isUserType(b.filter)) {
        const aName  = a.filter.givenName + ' ' + a.filter.familyName;
        const bName  = b.filter.givenName + ' ' + b.filter.familyName;
        return aName.toLocaleLowerCase().localeCompare(bName.toLocaleLowerCase());
      }
      return 0;
    })
    if (currentItems) {
      currentItems.forEach((item) => {
        if (isFilterType(item.filter)) {
          shareFilters.unshift(item)
        }
      })
      currentItems.forEach((item) => {
        if (isEveryoneType(item.filter)) {
          shareFilters.unshift(item);
        }
      })
    }
    return shareFilters
  }, [currentItems])

  const ContextMenu: React.FC<{ item: ShareFilter }> = ({ item }) => {
    const enableCustomDecksCollaborationEdit = useFeatureFlags('enableCustomDecksCollaborationEdit');
    return (
      <>
        <Iffy is={enableCustomDecksCollaborationEdit}>
          <DNAContextMenu>
            <DNAContextMenu.Anchor>
              <DNAButton
                appearance="outline"
                status="tertiary"
                iconRight="caret-down"
              >
                {`Can ${item.type.toLowerCase()}`}
              </DNAButton>
            </DNAContextMenu.Anchor>
            <DNAContextMenu.Items>
              <DNAContextMenu.Item
                key="edit"
                status="neutral"
                onPress={() => togglePermission(item.id)}
                title={`Can ${(item.type === PermissionType.VIEW
                  ? PermissionType.EDIT : PermissionType.VIEW).toLowerCase()}`}
              />
              <DNAContextMenu.Item
                key="remove"
                status="danger"
                onPress={() => onRemoveItem(item.id)}
                title="Remove"
              />
            </DNAContextMenu.Items>
          </DNAContextMenu>
        </Iffy>
        <Iffy is={!enableCustomDecksCollaborationEdit}>
          <DNAButton
            appearance="outline"
            status="tertiary"
            disabled={true}
          >
            Can view
          </DNAButton>
          <DNAButton
            appearance="ghost"
            status="subtle"
            iconLeft="x"
            onPress={() => onRemoveItem(item.id)}
          />
        </Iffy>
      </>
    )
  }

  return (
    <FlatList
      data={items.filter((item) => !item.isDeleted)}
      keyExtractor={({ id }) => id}
      ListEmptyComponent={(
        <DNABox>
          <DNAText status="subtle">Only you have access</DNAText>
        </DNABox>
      )}
      ItemSeparatorComponent={() => <DNADivider style={{ marginVertical: 12 }} />}
      scrollEnabled
      renderItem={({ item }) => {
        return (
          <DNABox appearance="row" spacing="between" fill alignY="center">
            {isEveryoneType(item.filter) &&
              <DNABox>
                <DNAText bold colorMode="dark" numberOfLines={1} style={styles.titleText}>
                  Everyone
                </DNAText>
              </DNABox>
            }
            {isUserType(item.filter) &&
              <DNABox testID="sharee-users-container" shrink appearance="col" alignY="center" fill>
                <DNAText bold colorMode="dark" numberOfLines={1} style={styles.titleText}>
                  {`${item.filter.givenName} ${item.filter.familyName}`}
                </DNAText>
                <DNAText style={styles.customFieldsText} numberOfLines={1}>
                  {item.filter.email}
                </DNAText>
              </DNABox>
            }
            {isFilterType(item.filter) &&
              <DNABox appearance={'col'} fill alignY="center">
                {Object.keys(item.filter).map(key => {
                  const fieldDefinition = indexedCustomFields[key];
                  const filters = item.filter[key] as LabelValues
                  const joinedValues = getValuesLabels(fieldDefinition, filters.values).join(', ')
                  return (
                    <DNABox appearance="row" key={key}>
                      <DNAText bold colorMode="dark" numberOfLines={3} style={styles.titleText}>
                        {`${fieldDefinition?.fieldLabel}:`}
                      </DNAText>
                      <DNAText style={[styles.customFieldsText, { marginLeft: 4, width: 300 }]}>{joinedValues}</DNAText>
                    </DNABox>
                  )
                })}
              </DNABox>
            }
            <DNABox appearance="row" alignY="center">
              <ContextMenu item={item} />
            </DNABox>
          </DNABox>
        )
      }}
    />
  )
})

function getValuesLabels(fieldDefinition: CustomFieldDefinition, values: string[]): string[] {
  const labels: string[] = [];
  fieldDefinition?.fieldValueDefinitions.forEach(({ id, value }) => {
    if (values.includes(id)) {
      labels.push(value);
    }
  });

  return labels;
}

interface ShareFolderProps {
  folderORM: FolderORM
  setMode: (mode: ShareFolderMode) => void
  currentItems: ShareFilter[]
  onRemoveItem: (id: string) => void
  onConfirm: () => void
  initialValues: ShareFilter[]
  display: boolean
  isDesktop: boolean
  onShareWithEveryone: () => void
  togglePermission: (id: string) => void
  isProcessing: boolean,
  sendEmailNotification: boolean,
  setSendEmailNotification: (value: boolean) => void,
}

const ShareFolder: DNAModalVariant<ShareFolderProps> = React.memo((props) => {
  const {
    folderORM,
    toggleModal,
    setMode,
    currentItems,
    onRemoveItem,
    onConfirm,
    onShareWithEveryone,
    initialValues,
    isDesktop,
    togglePermission,
    isProcessing,
    setSendEmailNotification,
    sendEmailNotification,
    display,
  } = props

  const canUnshare = initialValues.length > 0 && initialValues.some((item) => !item.isDeleted)
  const unshareOnly = initialValues?.some((item) => !item.isDeleted) && currentItems?.every((item) => item.isDeleted)

  const isSharedWithEveryone = currentItems?.some(({ targetType, isDeleted }) => {
    return targetType === ShareTargetType.ALL && !isDeleted;
  });

  return (
    <DNAModal style={{ display: display ? undefined : 'none', paddingVertical: 24 }}>
      <DNAModal.Header>
        {/* DESKTOP HEADER */}
        <Iffy is={isDesktop}>
          <DNABox
            testID="share-folder-modal-header"
            appearance="row"
            spacing="between"
            style={{ minWidth: 600 }}
            alignY="center"
          >
            <DNABox appearance="row" alignY="center">
              <DNABox appearance="col" alignY="center">
                <DNAText h4>Share folder</DNAText>
                <DNAText
                  status="subtle"
                  numberOfLines={2}
                  style={{ maxWidth: 450 }}
                >
                  {folderORM.model.name}
                </DNAText>
              </DNABox>
            </DNABox>
            <DNAButton
              testID="cancel-icon-button"
              appearance="ghost"
              status="subtle"
              iconLeft="x"
              onPress={toggleModal}
            />
          </DNABox>
        </Iffy>
        {/* TABLET HEADER */}
        <Iffy is={!isDesktop}>
          <DNABox
            testID="share-folder-modal-header"
            appearance="row"
            spacing="between"
            style={{ minWidth: 600 }}
            alignY="center"
          >
            <DNABox alignY="center">
              <DNAButton
                appearance="ghost"
                status="subtle"
                iconLeft="x"
                onPress={toggleModal}
                style={{ marginRight: 10 }}
              />
              <DNAText
                h4
                numberOfLines={2}
                style={{ maxWidth: 450 }}
              >
                {folderORM.model.name}
              </DNAText>
            </DNABox>
            <DNABox appearance="col" alignY="center" spacing="sm" alignX="end">
              <DNABox alignY="center" spacing="between">
                <Iffy is={canUnshare && !unshareOnly}>
                  <DNAButton
                    testID="unshare-button"
                    appearance="ghost"
                    status="primary"
                    size="xl"
                    onPress={() => setMode(ShareFolderMode.UNSHARE)}
                  >
                    Unshare
                  </DNAButton>
                </Iffy>
                <DNAButton
                  appearance="ghost"
                  status="primary"
                  size="xl"
                  onPress={onConfirm}
                  disabled={isEqual(initialValues, currentItems)}
                >
                  {unshareOnly ? 'Unshare' : 'Share'}
                </DNAButton>
              </DNABox>
              <Iffy is={!isEqual(initialValues, currentItems)}>
                <DNABox alignY="center">
                  <DNACheckbox
                    checked={sendEmailNotification}
                    onChange={setSendEmailNotification}
                  />
                  <DNAText style={{ marginHorizontal: 8 }}>Send email notification</DNAText>
                </DNABox>
              </Iffy>
            </DNABox>
          </DNABox>
        </Iffy>
      </DNAModal.Header>
      <DNAModal.Body>
        <DNABox appearance="col" fill style={styles.modalBody}>
          <DNABox appearance="row" spacing="sm">
            <DNAButton
              testID="add-people-button"
              appearance="outline"
              status="tertiary"
              onPress={() => setMode(ShareFolderMode.ADD_PEOPLE)}
            >
              Add people
            </DNAButton>
            <DNAButton
              testID="share-with-everyone-button"
              appearance="outline"
              status="tertiary"
              onPress={onShareWithEveryone}
              disabled={isSharedWithEveryone}
            >
              Share with everyone
            </DNAButton>
            <DNAButton
              testID="create-access-filter-button"
              appearance="outline"
              status="tertiary"
              onPress={() => setMode(ShareFolderMode.CREATE_ACCESS_FILTER)}
            >
              Create access filter
            </DNAButton>
          </DNABox>
          <DNADivider style={{ marginTop: 24 }} />
          <DNAText label style={{ marginTop: 24 }}>Who Has Access</DNAText>
          <DNABox style={{ marginTop: 16, height: 308 }}>
            <PermissionsList
              currentItems={currentItems}
              onRemoveItem={onRemoveItem}
              togglePermission={togglePermission}
            />
          </DNABox>
        </DNABox>
      </DNAModal.Body>
      {/* FOOTER */}
      <Iffy is={isDesktop}>
        <DNAModal.Footer>
          <DNABox appearance="row" spacing="between" style={{ width: '100%' }} fill>
            <DNABox>
              <Iffy is={canUnshare && !unshareOnly}>
                <DNAButton
                  testID="unshare-button"
                  appearance="outline"
                  onPress={() => setMode(ShareFolderMode.UNSHARE)}
                >
                  Unshare
                </DNAButton>
              </Iffy>
              <Iffy is={!isEqual(initialValues, currentItems)}>
                <DNABox alignY="center" style={{ marginLeft: 8 }}>
                  <DNACheckbox
                    checked={sendEmailNotification}
                    onChange={setSendEmailNotification}
                  />
                  <DNAText style={{ marginLeft: 8 }}>
                    Send email notification
                  </DNAText>
                </DNABox>
              </Iffy>
            </DNABox>
            <DNABox appearance="row" spacing="sm">
              <DNAButton
                testID="cancel-button"
                appearance="outline"
                status="tertiary"
                onPress={toggleModal}
              >
                Cancel
              </DNAButton>
              <DNAButton
                testID="share-folder-modal-confirmation-button"
                onPress={onConfirm}
                disabled={isEqual(initialValues, currentItems) || isProcessing}
              >
                {unshareOnly ? 'Unshare' : 'Share folder'}
              </DNAButton>
            </DNABox>
          </DNABox>
        </DNAModal.Footer>
      </Iffy>
    </DNAModal>
  )
})

export default ShareFolder
